import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';

const TYPE_LABELS: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'error' | 'default' }> = {
  gex_flip_negative: { label: 'GEX Flip', color: 'warning' },
  convergence_directional: { label: 'Directional Risk', color: 'warning' },
  iv_elevated: { label: 'IV Elevated', color: 'info' },
  price_entry_zone: { label: 'Entry Zone', color: 'success' },
  price_target_hit: { label: 'Target Hit', color: 'success' },
  price_stop_hit: { label: 'Stop Breached', color: 'error' },
};

const Alerts: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tickerFilter, setTickerFilter] = useState('');
  const pageSize = 50;

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (tickerFilter) params.ticker = tickerFilter.toUpperCase();
      const result = await userService.listAllNotifications(params);
      setItems(result.items);
      setTotal(result.total);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, tickerFilter]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const hasFilters = typeFilter !== 'all' || !!tickerFilter;

  const handleClearAll = async () => {
    const params: Record<string, string> = {};
    if (typeFilter !== 'all') params.type = typeFilter;
    if (tickerFilter) params.ticker = tickerFilter.toUpperCase();
    await userService.deleteNotifications(hasFilters ? params : undefined);
    fetchAlerts();
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Alerts</Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={handleClearAll}
          disabled={total === 0}
        >
          {hasFilters ? `Clear Filtered (${total})` : `Clear All (${total})`}
        </Button>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            <MenuItem value="all">All Types</MenuItem>
            {Object.entries(TYPE_LABELS).map(([key, { label }]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Ticker</InputLabel>
          <Select
            value={tickerFilter}
            label="Ticker"
            onChange={(e) => { setTickerFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All</MenuItem>
            {/* Unique tickers from current page — not perfect but good enough */}
            {Array.from(new Set(items.map((i: any) => i.ticker))).sort().map((t: string) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">{total} alert{total !== 1 ? 's' : ''}</Typography>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info">No alerts.</Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((n) => {
                  const typeInfo = TYPE_LABELS[n.type] || { label: n.type, color: 'default' as const };
                  return (
                    <TableRow
                      key={n.id}
                      hover
                      sx={{ cursor: n.link ? 'pointer' : 'default', opacity: n.read ? 0.7 : 1 }}
                      onClick={() => n.link && navigate(n.link)}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">{formatTime(n.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={typeInfo.label} size="small" color={typeInfo.color} variant={n.read ? 'outlined' : 'filled'} />
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{n.ticker}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{n.title}</Typography></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{n.message}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
              <Typography variant="body2" sx={{ lineHeight: '30px' }}>Page {page} of {totalPages}</Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Alerts;
